export const themes = {
  light: {
    name: "Light",
    colors: {
      // Background colors - Clean light theme
      background: "#F8FAFC", // Main app background - very light blue-gray
      surface: "#F9FAFC", // Main surface - slightly warmer than background
      "surface-hover": "#F1F5F9", // Hover state surfaces - subtle gray-blue
      "surface-active": "#E2E8F0", // Active/pressed surfaces
      "surface-dark": "#E5E7EB", // Darker surface variant
      "surface-elevated": "#FFFFFF", // Elevated/modal surfaces

      // Additional surface colors
      "surface-card": "#FFFFFF", // Card backgrounds - white cards
      "surface-card-hover": "#F8FAFC", // Card hover states
      "surface-stat": "#FFFFFF", // StatCard surfaces
      "surface-input": "#FFFFFF", // Input/form backgrounds
      "surface-splash": "#F8FAFC", // Splash screen background
      "surface-border": "#E2E8F0", // Border colors - light gray-blue (slate-200)

      // Text/Content colors - matching reference design
      muted: "#64748B", // Secondary text, placeholders - slate-500
      "text-primary": "#0F172A", // Primary text - slate-900
      "text-secondary": "#475569", // Secondary text - slate-600
      "text-on-color": "#FFFFFF", // Text on colored backgrounds - always white

      // Brand/Accent colors
      primary: "#3B82F6", // Primary brand color - bright blue (consistent)
      "primary-dark": "#2563EB", // Darker primary variant
      "primary-light":  "#60A5FA", // Lighter primary variant
      error: "#DC2626", // Error states, destructive actions
      success: "#059669", // Success states, confirmations
      info: "#0891B2", // Info states, highlights
      warning: "#D97706", // Warning states
    }
  },
  premium: {
    name: "Premium",
    colors: {
      // Background colors - elegant professional theme with rich depths
      background: "#0d1117", // Rich dark charcoal - sophisticated base
      surface: "#21262d", // Premium charcoal surface - refined depth
      "surface-hover": "#30363d", // Elegant hover state - subtle warmth
      "surface-active": "#484f58", // Premium active state - polished feel
      "surface-dark": "#090c10", // Deepest premium black - luxury depth
      "surface-elevated": "#2d333b", // Elevated premium surface - professional lift

      // Additional surface colors with premium luxury palette
      "surface-card": "#1c2128", // Premium card backgrounds - sophisticated contrast
      "surface-card-hover": "#373e47", // Premium card hover - refined interaction
      "surface-stat": "#3d444d", // Premium metrics surfaces - executive dashboard
      "surface-input": "#161b22", // Premium input backgrounds - clean professional
      "surface-splash": "#0a0d12", // Premium splash - deep luxury
      "surface-border": "#30363d", // Premium borders - subtle, blends with surface

      // Text/Content colors with premium contrast
      muted: "#8b949e", // Premium muted text - refined gray
      "text-primary": "#f0f6fc", // Premium primary text - crisp white
      "text-secondary": "#d0d7de", // Premium secondary text - elegant gray
      "text-on-color": "#FFFFFF", // Text on colored backgrounds

      // Brand/Accent colors - luxurious professional palette
      primary: "#238636", // Premium primary - sophisticated forest green
      "primary-dark": "#1a7f37", // Darker premium primary - rich depth
      "primary-light": "#2ea043", // Lighter premium primary - vibrant accent
      error: "#da3633", // Premium error - refined red
      success: "#238636", // Premium success - matching brand green
      info: "#1f6feb", // Premium info - professional blue
      warning: "#fb8500", // Premium warning - elegant amber
    }
  }
} as const;

export type ThemeName = keyof typeof themes;
export type ThemeColors = typeof themes.light.colors;

export const applyTheme = (themeName: ThemeName) => {
  const theme = themes[themeName];
  const root = document.documentElement;

  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key}`, value);
  });

  // Set data-theme attribute for theme-specific styling
  root.setAttribute('data-theme', themeName);

  // Store theme preference
  localStorage.setItem('theme', themeName);
};

export const getStoredTheme = (): ThemeName => {
  const stored = localStorage.getItem('theme') as ThemeName;
  return Object.keys(themes).includes(stored) ? stored : 'light';
};